import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, googleProvider } from '../config/firebase';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { createAuthHeaders } from '../utils/authUtils';
import { clearAllCache } from '../utils/cacheUtils';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [authorizationLoading, setAuthorizationLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        // Check business access when user signs in
        await checkBusinessAccess(user);
      } else {
        // Clear authorization when user signs out
        setIsAuthorized(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const checkBusinessAccess = async (user) => {
    try {
      setAuthorizationLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL}/verifyBusinessAccess`, {
        method: 'POST',
        headers: await createAuthHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify business access');
      }

      setIsAuthorized(data.authorized);
      
    } catch (err) {
      console.error('Business access verification error:', err);
      setError('Failed to verify access permissions');
      setIsAuthorized(false);
    } finally {
      setAuthorizationLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, googleProvider);
      // checkBusinessAccess will be called automatically via useEffect
      return result;
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError(getErrorMessage(err.code));
      throw err;
    }
  };

  const signInWithEmail = async (email, password) => {
   try {
     setError(null);
     const result = await signInWithEmailAndPassword(auth, email, password);
     return result;
   } catch (err) {
     console.error('Email sign-in error:', err);
     setError(getErrorMessage(err.code));
     throw err;
   }
 };
 
 const signUpWithEmail = async (email, password) => {
   try {
     setError(null);
     const result = await createUserWithEmailAndPassword(auth, email, password);
     return result;
   } catch (err) {
     console.error('Email sign-up error:', err);
     setError(getErrorMessage(err.code));
     throw err;
   }
 };

  const logout = async () => {
    try {
      setError(null);
      setIsAuthorized(null);
      clearAllCache();
      await signOut(auth);
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to sign out');
      throw err;
    }
  };

  const getIdToken = async () => {
    if (user) {
      try {
        return await user.getIdToken();
      } catch (err) {
        console.error('Error getting ID token:', err);
        return null;
      }
    }
    return null;
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled.';
      case 'auth/popup-blocked':
        return 'Popup was blocked. Please allow popups and try again.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/too-many-requests':
        return 'Too many requests. Please try again later.';
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      default:
        return 'Sign-in failed. Please try again.';
    }
  };

  const value = {
    user,
    isAuthorized,
    authorizationLoading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    logout,
    getIdToken,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};